import React, { useState } from "react";
import ImageUpload from "./ImageUpload";
import { uploadCharacterImage } from "../utils/characterUtils";

const CharacterCreationForm = ({ onCreateCharacter, onCancel, isLoading = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    personality_prompt: "",
    system_prompt: "",
    is_public: false,
    status_emoji: "🔥",
    short_description: "",
    creator_name: ""
  });

  const [errors, setErrors] = useState({});
  const [avatarImage, setAvatarImage] = useState(null);
  const [selfieImages, setSelfieImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Available status emojis for selection
  const statusEmojis = ["🔥", "🧪", "⭐", "💫", "🚀", "🎭", "🤖", "💎"];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Character name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Character name must be at least 2 characters";
    }
    
    if (!formData.description.trim()) {
      newErrors.description = "Character description is required";
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }
    
    if (!formData.personality_prompt.trim()) {
      newErrors.personality_prompt = "Personality description is required";
    } else if (formData.personality_prompt.trim().length < 20) {
      newErrors.personality_prompt = "Personality description must be at least 20 characters";
    }

    // Validate publication fields if character is being published
    if (formData.is_public) {
      if (!formData.short_description.trim()) {
        newErrors.short_description = "Short description is required for published characters";
      } else if (formData.short_description.trim().length < 10) {
        newErrors.short_description = "Short description must be at least 10 characters";
      } else if (formData.short_description.trim().length > 150) {
        newErrors.short_description = "Short description must be less than 150 characters";
      }
      
      if (!formData.creator_name.trim()) {
        newErrors.creator_name = "Creator name is required for published characters";
      } else if (formData.creator_name.trim().length < 2) {
        newErrors.creator_name = "Creator name must be at least 2 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Trim all values and set default base_character to "sam"
      const characterData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        personality_prompt: formData.personality_prompt.trim(),
        system_prompt: formData.system_prompt.trim() || "",
        base_character: "sam", // Default to sam since we're keeping it simple
        is_public: formData.is_public,
        status_emoji: formData.status_emoji,
        short_description: formData.short_description.trim(),
        creator_name: formData.creator_name.trim()
      };
      
      // Call the parent's onCreateCharacter function
      // This should return the created character data including character_id
      const result = await onCreateCharacter(characterData);
      
      // If character creation was successful and we have images to upload
      if (result && result.character_id && (avatarImage || selfieImages.length > 0)) {
        await handleImageUploads(result.character_id, result.user_id || localStorage.getItem('userId'));
      }
    }
  };

  const handleImageUploads = async (characterId, userId) => {
    setUploadingImages(true);
    
    try {
      // Upload avatar if present
      if (avatarImage) {
        await uploadCharacterImage(characterId, userId, avatarImage, 'avatar');
      }
      
      // Upload selfies if present
      for (const selfieImage of selfieImages) {
        await uploadCharacterImage(characterId, userId, selfieImage, 'selfie');
      }
      
      console.log('All images uploaded successfully');
    } catch (error) {
      console.error('Error uploading images:', error);
      // Note: Character is already created, so we just log the error
      // In a production app, you might want to show a warning to the user
    } finally {
      setUploadingImages(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      description: "",
      personality_prompt: "",
      system_prompt: "",
      is_public: false,
      status_emoji: "🔥",
      short_description: "",
      creator_name: ""
    });
    setErrors({});
    setAvatarImage(null);
    setSelfieImages([]);
    setUploadingImages(false);
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-semibold text-white mb-6">Create New Character</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Character Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
              Character Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? "border-red-500" : "border-gray-600"
              }`}
              placeholder="Enter character name"
              disabled={isLoading}
            />
            {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Character Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
              Character Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                errors.description ? "border-red-500" : "border-gray-600"
              }`}
              placeholder="Describe your character's role and purpose"
              disabled={isLoading}
            />
            {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
          </div>

          {/* Personality Prompt */}
          <div>
            <label htmlFor="personality_prompt" className="block text-sm font-medium text-gray-300 mb-2">
              Personality & Behavior *
            </label>
            <textarea
              id="personality_prompt"
              name="personality_prompt"
              value={formData.personality_prompt}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                errors.personality_prompt ? "border-red-500" : "border-gray-600"
              }`}
              placeholder="Describe how your character should behave, their personality traits, communication style, etc."
              disabled={isLoading}
            />
            {errors.personality_prompt && <p className="text-red-400 text-sm mt-1">{errors.personality_prompt}</p>}
          </div>

          {/* System Prompt (Optional) */}
          <div>
            <label htmlFor="system_prompt" className="block text-sm font-medium text-gray-300 mb-2">
              Advanced Instructions (Optional)
            </label>
            <textarea
              id="system_prompt"
              name="system_prompt"
              value={formData.system_prompt}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
              placeholder="Additional system instructions or constraints (optional)"
              disabled={isLoading}
            />
          </div>

          {/* Character Visibility Section */}
          <div className="border-t border-gray-600 pt-6">
            <h3 className="text-lg font-medium text-white mb-4">Character Visibility</h3>
            
            {/* Publish to Character Board Toggle */}
            <div className="mb-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="sr-only"
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                  formData.is_public ? 'bg-blue-600' : 'bg-gray-600'
                }`}>
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ease-in-out ${
                    formData.is_public ? 'transform translate-x-5' : ''
                  }`}></div>
                </div>
                <span className="ml-3 text-sm text-gray-300">
                  Publish to Character Board
                </span>
              </label>
              <p className="text-gray-400 text-xs mt-1 ml-14">
                Make this character discoverable by all users on the public character board
              </p>
            </div>

            {/* Publication Fields - Only show when character is set to public */}
            {formData.is_public && (
              <div className="space-y-4 ml-4 border-l-2 border-blue-600 pl-4">
                {/* Status Emoji Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Status Emoji
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {statusEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, status_emoji: emoji }))}
                        className={`w-10 h-10 text-xl rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${
                          formData.status_emoji === emoji
                            ? 'border-blue-500 bg-blue-900 bg-opacity-30'
                            : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                        }`}
                        disabled={isLoading}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  <p className="text-gray-400 text-xs mt-1">
                    Choose an emoji to represent your character's status or theme
                  </p>
                </div>

                {/* Short Description */}
                <div>
                  <label htmlFor="short_description" className="block text-sm font-medium text-gray-300 mb-2">
                    Short Description for Character Board *
                  </label>
                  <textarea
                    id="short_description"
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleInputChange}
                    rows={2}
                    maxLength={150}
                    className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                      errors.short_description ? "border-red-500" : "border-gray-600"
                    }`}
                    placeholder="Brief description for the character board (max 150 characters)"
                    disabled={isLoading}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.short_description && <p className="text-red-400 text-xs">{errors.short_description}</p>}
                    <p className="text-gray-400 text-xs ml-auto">
                      {formData.short_description.length}/150
                    </p>
                  </div>
                </div>

                {/* Creator Name */}
                <div>
                  <label htmlFor="creator_name" className="block text-sm font-medium text-gray-300 mb-2">
                    Creator Name *
                  </label>
                  <input
                    type="text"
                    id="creator_name"
                    name="creator_name"
                    value={formData.creator_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.creator_name ? "border-red-500" : "border-gray-600"
                    }`}
                    placeholder="Your name or display name as the creator"
                    disabled={isLoading}
                  />
                  {errors.creator_name && <p className="text-red-400 text-xs mt-1">{errors.creator_name}</p>}
                </div>

                {/* Character Board Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Character Board Preview
                  </label>
                  <div className="bg-gray-700 border border-gray-600 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden">
                        {avatarImage ? (
                          <img 
                            src={avatarImage.preview} 
                            alt="Character preview" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-400 text-xs">No Image</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="text-white font-medium truncate">
                            {formData.name || 'Character Name'}
                          </h4>
                          <span className="text-lg">{formData.status_emoji}</span>
                          <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                            Community
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm mb-2 line-clamp-2">
                          {formData.short_description || 'Short description will appear here...'}
                        </p>
                        <p className="text-gray-400 text-xs">
                          by {formData.creator_name || 'Creator Name'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-xs mt-1">
                    This is how your character will appear on the Character Discovery Board
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Character Images Section */}
          <div className="border-t border-gray-600 pt-6">
            <h3 className="text-lg font-medium text-white mb-4">Character Images</h3>
          
          {/* Character Avatar Upload */}
          <ImageUpload
            type="avatar"
            onImageSelect={setAvatarImage}
            maxSize={1}
            disabled={isLoading || uploadingImages}
          />

          {/* Character Selfie Upload */}
          <ImageUpload
            type="selfie"
            onImageSelect={(imageData) => {
              if (imageData) {
                setSelfieImages(prev => [...prev, imageData]);
              }
            }}
            maxSize={5}
            disabled={isLoading || uploadingImages}
          />

          {/* Show selfie images */}
          {selfieImages.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Uploaded Selfies ({selfieImages.length})
              </label>
              <div className="grid grid-cols-2 gap-2">
                {selfieImages.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image.preview}
                      alt={`Selfie ${index + 1}`}
                      className="w-full h-20 object-cover rounded"
                    />
                    <button
                      type="button"
                      onClick={() => setSelfieImages(prev => prev.filter((_, i) => i !== index))}
                      className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      disabled={isLoading || uploadingImages}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={isLoading || uploadingImages}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Creating...
                </>
              ) : uploadingImages ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Uploading Images...
                </>
              ) : (
                "Create Character"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CharacterCreationForm;